import { useEffect, useMemo } from "react";
import {
  Navigate,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import { RecurConfigEx } from "../../../api/Person";
import {
  useGetRecurList,
  useGetSubscriptionRecurInstances,
} from "../../../api/PersonApi";
import { usePermissionValidator } from "../../../api/UserPermissionValidators";
import Button, {
  ButtonColorTypes,
  ButtonTypes,
} from "../../../shared-components/Button";
import { LazyLoadTable } from "../../../shared-components/LazyLoadTable";
import LoadingSpinner from "../../../shared-components/LoadingSpinner";
import Pagination from "../../../shared-components/Pagination";
import SubscriptionLazyRow from "./SubscriptionLazyRow";
import { SubscriptionRecurInstanceEx } from "../../../api/Person";

const headers = ["Completed Date", "Total Flags", "Report ID", "Download PDF"];

type SubscriptionRow = SubscriptionRecurInstanceEx & {
  showAllRows: boolean;
  rowKey: string;
};

const SubscriptionDetails = () => {
  const {
    companyId: companyUUID,
    subid: subCompanyUUID,
    recurUuid: recurUUID,
  } = useParams();
  const { search } = useLocation();
  const searchParams = useMemo(() => new URLSearchParams(search), [search]);
  const showAllRows = searchParams.get("all") === "1";
  // Derived from the URL rather than held in state: navigating to another
  // subscription clears the page param, so the page can never lag a render
  // behind `recurUUID` and trigger a fetch for the previous page.
  const currentPage = useMemo(() => {
    const pageFromUrl = parseInt(searchParams.get("page") ?? "", 10);
    return Number.isFinite(pageFromUrl) && pageFromUrl > 0 ? pageFromUrl : 1;
  }, [searchParams]);
  const pageSize = 10;
  const navigate = useNavigate();
  const permissions = usePermissionValidator();

  const {
    data: recurCheck,
    mutate: getRecurCheck,
    isLoading: loadingRecurCheck,
    isSuccess: loadedRecurCheck,
    reset: resetRecurCheck,
  } = useGetRecurList();
  const {
    data: recurInstances,
    mutate: getRecurInstances,
    isLoading: loadingRecurInstances,
    isError: recurInstancesFailed,
    reset: resetRecurInstances,
  } = useGetSubscriptionRecurInstances();
  const activeCompanyUUID = subCompanyUUID ? subCompanyUUID : companyUUID;

  // Users without view-all reports only reach subscriptions they requested.
  const individual = !permissions.canViewAllReportsWithinCompany;

  const recurList = useMemo(() => {
    return recurCheck?.data ?? [];
  }, [recurCheck]);

  // Derived during render so a stale response can never be shown for the
  // subscription currently in the URL.
  const rc: RecurConfigEx | undefined = useMemo(() => {
    return recurList.find((item) => item.recur_uuid === recurUUID);
  }, [recurList, recurUUID]);

  // Drop previous subscription header/table data when the route target changes.
  useEffect(() => {
    resetRecurCheck();
    resetRecurInstances();
  }, [activeCompanyUUID, recurUUID, resetRecurCheck, resetRecurInstances]);

  useEffect(() => {
    if (!activeCompanyUUID || !recurUUID) {
      return;
    }
    getRecurCheck({
      company_uuid: activeCompanyUUID,
      recur_uuids: recurUUID,
      individual,
    });
  }, [activeCompanyUUID, recurUUID, getRecurCheck, individual]);

  // Fetch from the route UUID (not `rc`) so the request starts in the same
  // effect cycle as the subscription header fetch, not one render later.
  useEffect(() => {
    if (!recurUUID) {
      return;
    }
    getRecurInstances({
      recur_uuid: recurUUID,
      page: currentPage,
      size: pageSize,
    });
  }, [recurUUID, currentPage, pageSize, getRecurInstances]);

  // After reset, data is undefined until the first response — treat that as
  // loading so LazyLoadTable does not flash the empty state. Exclude error:
  // a failed mutation leaves data undefined with isLoading false, which would
  // otherwise spin forever.
  const instancesLoading =
    loadingRecurInstances ||
    (recurInstances === undefined && !recurInstancesFailed);

  const tableRows = useMemo<SubscriptionRow[]>(() => {
    if (instancesLoading || !rc) {
      return [];
    }
    return (recurInstances?.data ?? []).map((row) => ({
      ...row,
      showAllRows,
      // Remount when request_uuid changes even if start_date stays the same.
      rowKey: `${row.start_date}:${row.request_uuid ?? "scheduled"}`,
    }));
  }, [recurInstances?.data, showAllRows, instancesLoading, rc]);

  // A subscription outside the user's scope comes back as an empty page, not an error.
  if (loadedRecurCheck && !loadingRecurCheck && recurList.length === 0) {
    return <Navigate to="/unauthorized" replace />;
  }

  return (
    <div>
      <Button
        className="px-0 py-2"
        text={`Back to List`}
        type={ButtonTypes.back}
        colorType={ButtonColorTypes.custom}
        onClick={() => {
          navigate(
            subCompanyUUID
              ? `/companies/${companyUUID}/subcompany/${subCompanyUUID}/settings/subscriptions`
              : `/companies/${companyUUID}/settings/subscriptions`
          );
        }}
      ></Button>
      <div>
        {loadingRecurCheck || !rc ? (
          <LoadingSpinner />
        ) : (
          <div className="w-[800px]">
            <div className="p-2 mb-6">
              <div className="text-xl font-semibold">{rc.full_name}</div>
              <div className="text-xs text-gray-400">{rc.recur_uuid}</div>
            </div>
            <div className="flex justify-between p-6 border rounded-lg mb-6">
              <span>
                <span className="font-semibold mr-1">Check Type: </span>
                <span className="text-gray-500">
                  {rc.check_types[0].blueprint_name}
                </span>
              </span>
              <span>
                <span className="font-semibold mr-1">Behavior Kit: </span>
                <span className="text-gray-500">
                  {rc.check_types[0].behavior_kit_name ?? "General"}
                </span>
              </span>
              <span>
                <span className="font-semibold mr-1">Frequency:</span>
                <span className="text-gray-500">
                  Every{" "}
                  {Number(rc.schedule_value) <= 1
                    ? "month"
                    : `${rc.schedule_value} months`}
                </span>
              </span>
            </div>
            <LazyLoadTable
              parentRows={tableRows}
              parentLoading={instancesLoading}
              headers={
                permissions.canDownloadPDF ? headers : headers.slice(0, 3)
              }
              emptyMessage={
                recurInstancesFailed
                  ? "Error loading historical reports"
                  : "No Historical Report Found"
              }
              keyField={"rowKey"}
              rowFunction={SubscriptionLazyRow}
            />
            <Pagination
              pageSize={pageSize}
              currentPage={currentPage}
              totalCount={recurInstances?.total ?? 1}
              search={""}
              params={showAllRows ? { all: "1" } : undefined}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default SubscriptionDetails;
